import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  IconButton,
  DialogContent,
  Typography,
  DialogTitle, Button, Box, TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { useDispatch } from 'react-redux';
import localization from '../../../localization';
import CodeInput from '../../Inputs/CodeInput';
import { loginTwoFA, loginWithBackupCode } from '../../../redux/actions/Account';
import { Formik } from 'formik';

const STEP = {
  CODE: 'CODE',
  BACKUP: 'BACKUP',
};

const TwoFALoginPopup = ({
  open,
  token,
  realm,
  onClose = () => { },
  onSuccess = () => { },
}) => {
  const dispatch = useDispatch();
  const [step, setStep] = useState(STEP.CODE);
  const [code, setCode] = useState('');
  const [isVerifyLoading, setIsVerifyLoading] = useState(false);
  const [isCodeValid, setIsCodeValid] = useState(true);

  const onVerifyCodeClick = (e) => {
    e.preventDefault();
    setIsVerifyLoading(true);

    dispatch(loginTwoFA({
      credentials: {
        realm,
        token,
        code,
      },
      onError: () => {
        setIsCodeValid(false);
        setIsVerifyLoading(false);
      },
      onSuccess: () => {
        onSuccess();
        setIsVerifyLoading(false);
      },
    }));
  };

  const onVerifyBackupCodeClick = ({ email, backupCode }) => {
    setIsVerifyLoading(true);

    dispatch(loginWithBackupCode({
      credentials: {
        realm,
        email,
        backupCode,
      },
      onError: () => {
        setIsCodeValid(false);
        setIsVerifyLoading(false);
      },
      onSuccess: () => {
        onSuccess();
        setIsVerifyLoading(false);
      },
    }));
  };

  const onChangeCode = (value) => {
    setCode(value);
    setIsCodeValid(true);
  };

  const onChangeStep = (nextStep) => {
    setStep(nextStep);
    setCode('');
    setIsCodeValid(true);
  };

  const renderErrorMessage = () => (
    <Typography color="#e47575" sx={{ fontSize: '13px' }}>
      {localization.t('forms.messages.twoFAError')}
    </Typography>
  );

  const renderCodeStep = () => (
    <DialogContent>
      <Typography>
        {localization.t('forms.subHeaders.twoFALoginCode')}
      </Typography>
      <Box display="flex" gap="10px" marginTop="15px" marginBottom="5px" alignItems="center">
        <form onSubmit={onVerifyCodeClick}>
          <CodeInput
            value={code}
            onChange={onChangeCode}
            isValid={isCodeValid}
          />
          <Button
            type='submit'
            variant="contained"
            size="medium"
            disabled={isVerifyLoading}
          >
            {localization.t('forms.buttons.submit')}
          </Button>
        </form>
      </Box>
      {!isCodeValid && renderErrorMessage()}
      <Typography
        onClick={() => onChangeStep(STEP.BACKUP)}
        sx={{ cursor: 'pointer', marginTop: '20px' }}
        dangerouslySetInnerHTML={{ __html: localization.t('forms.text.twoFALoginUseBackup') }}
      />
    </DialogContent>
  );

  const renderBackupStep = () => (
    <DialogContent key="backupCodeDialog">
      <Formik
        initialValues={{ email: '', backupCode: '' }}
        onSubmit={onVerifyBackupCodeClick}
        validate={(values) => {
          const errors = {};

          if (!values.email || !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
            errors.email = 'Invalid email address';
          }

          if (!values.backupCode) {
            errors.backupCode = 'Backup code is required'
          }

          return errors;
        }}
      >
        {({
          errors,
          handleChange,
          handleSubmit,
          touched,
          values,
        }) => (
          <form onSubmit={handleSubmit}>
            <Typography>
              {localization.t('forms.subHeaders.twoFALoginBackup')}
            </Typography>
            <TextField
              name="email"
              variant="outlined"
              label="Email"
              value={values.email}
              onChange={handleChange}
              size="small"
              margin="normal"
              fullWidth
              sx={{ maxWidth: '80%' }}
              error={touched.email && errors.email}
            />
            {touched.email && errors.email && <Typography color="#e47575" sx={{ fontSize: '13px' }}>
              {errors.email}
            </Typography>}
            <TextField
              name='backupCode'
              variant="outlined"
              label="Backup Code"
              value={values.backupCode}
              onChange={handleChange}
              size="small"
              margin="normal"
              fullWidth
              sx={{ maxWidth: '80%' }}
              error={!isCodeValid || touched.email && errors.backupCode}
            />
            {!isCodeValid && renderErrorMessage()}
            <Box display="flex" gap="10px" marginTop="15px" marginBottom="5px" alignItems="center">
              <Button
                type='submit'
                variant="contained"
                size="medium"
                disabled={isVerifyLoading}
              >
                {localization.t('forms.buttons.submit')}
              </Button>
              <Typography
                onClick={() => onChangeStep(STEP.CODE)}
                sx={{ cursor: 'pointer' }}
                dangerouslySetInnerHTML={{ __html: localization.t('forms.text.twoFALoginUseCode') }}
              />
            </Box>
          </form>)}
      </Formik>
    </DialogContent>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle display="flex" justifyContent="space-between">
        <Typography variant="h4">
          {localization.t('forms.headers.twoFALoginTitle')}
        </Typography>
        <IconButton
          sx={{ padding: 0 }}
          aria-label="close"
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {step === STEP.CODE && renderCodeStep()}
      {step === STEP.BACKUP && renderBackupStep()}
    </Dialog>
  );
};

TwoFALoginPopup.propTypes = {
  open: PropTypes.bool,
  token: PropTypes.string,
  realm: PropTypes.string,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
};

export default TwoFALoginPopup;
